import { getFeatureFlagsQueryOptions, setFeatureFlagMutationOptions } from '@gozoki/api'
import { RBAC_ENFORCEMENT_FEATURE_FLAG } from '@gozoki/api-types'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Shield, type LucideIcon } from 'lucide-react'

import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { Title } from '~/components/new/app-ui/title'
import { RbacAccessDenied } from '~/components/routes/administration/roles/access-denied'
import { Card, CardDescription, CardHeader, CardTitle } from '~/components/ui/card'
import { Label } from '~/components/ui/label'
import { Skeleton } from '~/components/ui/skeleton'
import { Switch } from '~/components/ui/switch'
import { useRights } from '~/utils/hooks'

export const Route = createFileRoute('/_authenticated/feature-flags')({
  component: FeatureFlagsPage,
})

/** Back-office copy + metadata for flags we expose in the UI (extend when adding flags). */
interface FeatureFlagRegistryEntry {
  /** Must match `feature_flags.name` / api-types constants. */
  name: string
  title: string
  description: string
  icon: LucideIcon
}

function featureFlagStatusLabel(isLoading: boolean, enabled: boolean): string {
  if (isLoading) {
    return '…'
  }
  if (enabled) {
    return 'Activé'
  }
  return 'Désactivé'
}

const FEATURE_FLAG_REGISTRY: FeatureFlagRegistryEntry[] = [
  {
    name: RBAC_ENFORCEMENT_FEATURE_FLAG,
    title: 'Appliquer le RBAC sur l’API',
    description:
      'Lorsque ce réglage est activé, les accès aux routes protégées sont filtrés selon les rôles et permissions. Si vous le désactivez, l’ancien comportement (droits legacy) s’applique — à utiliser avec prudence en production.',
    icon: Shield,
  },
]

function FeatureFlagsPage() {
  const isSuperAdmin = useRights(['SUPER_ADMIN'])
  const { data: flags = [], isLoading } = useQuery(getFeatureFlagsQueryOptions())
  const setFlag = useMutation(setFeatureFlagMutationOptions())

  if (!isSuperAdmin) {
    return <RbacAccessDenied />
  }

  return (
    <>
      <AppHeader>
        <Title>Feature flags</Title>
      </AppHeader>

      <AppContainer className="w-full min-w-0 space-y-6">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Paramètres runtime</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Activez ou désactivez des comportements sur l’API sans redéployer. Réservé aux
            super-admins.
          </p>
        </div>

        <Card variant="outline" className="gap-0 overflow-hidden p-0">
          <CardHeader className="border-border bg-muted/30 border-b px-5 py-4">
            <CardTitle className="text-sm font-semibold">Liste des indicateurs</CardTitle>
            <CardDescription>
              Chaque interrupteur met à jour la base via l’API ; les changements sont pris en compte
              immédiatement.
            </CardDescription>
          </CardHeader>

          <ul className="divide-border divide-y">
            {FEATURE_FLAG_REGISTRY.map((entry) => {
              const Icon = entry.icon
              const enabled = flags.find((f) => f.name === entry.name)?.enabled ?? false
              const togglingThis = setFlag.isPending && setFlag.variables?.name === entry.name
              const switchDisabled = isLoading || togglingThis
              const statusLabel = featureFlagStatusLabel(isLoading, enabled)

              return (
                <li key={entry.name}>
                  <div className="hover:bg-muted/20 flex flex-col gap-4 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 flex-1 gap-3">
                      <div
                        className="text-primary bg-primary/10 flex size-10 shrink-0 items-center justify-center rounded-2xl"
                        aria-hidden
                      >
                        <Icon className="size-5" />
                      </div>
                      <div className="min-w-0 space-y-1">
                        <Label htmlFor={`flag-${entry.name}`} className="text-base font-medium">
                          {entry.title}
                        </Label>
                        <p className="text-muted-foreground text-sm leading-snug">
                          {entry.description}
                        </p>
                        <p className="text-muted-foreground/80 font-mono text-xs">{entry.name}</p>
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-3 sm:flex-col sm:items-end">
                      {isLoading ? (
                        <Skeleton className="h-5 w-12 rounded-full" />
                      ) : (
                        <Switch
                          id={`flag-${entry.name}`}
                          size="lg"
                          checked={enabled}
                          disabled={switchDisabled}
                          onCheckedChange={(checked) =>
                            setFlag.mutate({
                              name: entry.name,
                              enabled: checked === true,
                            })
                          }
                        />
                      )}
                      <span className="text-muted-foreground text-xs font-medium tabular-nums">
                        {statusLabel}
                      </span>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        </Card>
      </AppContainer>
    </>
  )
}
